<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({ filterType: 'accessible', teamIds: [] }),
  },
});

const emit = defineEmits(['update:modelValue']);

const teamList = useMapGetter('teams/getTeams');

// --- Local state ---
const showSettings = ref(false);
const showFilterDropdown = ref(false);

// --- Filter options ---
const filterOptions = [
  { value: 'all', label: 'CANNED_MGMT.SEARCH_SETTINGS.ALL' },
  { value: 'global', label: 'CANNED_MGMT.SEARCH_SETTINGS.GLOBAL' },
  { value: 'accessible', label: 'CANNED_MGMT.SEARCH_SETTINGS.ACCESSIBLE' },
  { value: 'teams', label: 'CANNED_MGMT.SEARCH_SETTINGS.TEAMS' },
];

const currentFilterLabel = computed(() => {
  const option = filterOptions.find(
    o => o.value === props.modelValue.filterType
  );
  return option ? option.label : 'CANNED_MGMT.SEARCH_SETTINGS.ACCESSIBLE';
});

const selectedTeams = computed({
  get: () => {
    const teams = teamList.value || [];
    return teams.filter(t => props.modelValue.teamIds.includes(t.id));
  },
  set: newVal => {
    emit('update:modelValue', {
      ...props.modelValue,
      teamIds: newVal.map(t => t.id),
    });
  },
});

// --- Methods ---
const toggleSettings = () => {
  showSettings.value = !showSettings.value;
};

const closeFilterDropdown = () => {
  showFilterDropdown.value = false;
};

const selectFilterType = type => {
  emit('update:modelValue', {
    ...props.modelValue,
    filterType: type,
    // Reset teamIds when switching away from 'teams'
    ...(type !== 'teams' ? { teamIds: [] } : {}),
  });
  showFilterDropdown.value = false;
};
</script>

<template>
  <div class="border-b border-n-strong bg-n-solid-1">
    <!-- Expandable toggle -->
    <button
      class="flex items-center justify-between w-full px-2 py-1.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-black2 cursor-pointer select-none"
      @click="toggleSettings"
    >
      <span>{{
        showSettings
          ? $t('CANNED_MGMT.SEARCH_SETTINGS.HIDE')
          : $t('CANNED_MGMT.SEARCH_SETTINGS.SHOW')
      }}</span>
      <svg
        class="w-3 h-3 transition-transform"
        :class="{ 'rotate-180': !showSettings }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7"
        />
      </svg>
    </button>

    <!-- Settings panel -->
    <div
      v-if="showSettings"
      class="flex flex-col gap-4 px-2 py-1.5 text-xs border-t border-n-strong"
    >
      <div class="flex items-center gap-2">
        <span class="whitespace-nowrap text-n-slate-11">{{
          $t('CANNED_MGMT.SEARCH_SETTINGS.DISPLAY_LABEL')
        }}</span>

        <!-- Filter dropdown -->
        <div class="relative">
          <button
            class="flex items-center gap-1 px-2 py-0.5 text-xs rounded border border-n-strong bg-n-solid-2 text-n-slate-11 hover:bg-n-alpha-black2 whitespace-nowrap"
            @click.stop="showFilterDropdown = !showFilterDropdown"
          >
            {{ $t(currentFilterLabel) }}
            <svg
              class="w-3 h-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          <div
            v-if="showFilterDropdown"
            v-on-clickaway="closeFilterDropdown"
            class="absolute left-0 top-full z-50 mt-1 bg-n-solid-1 border border-n-strong rounded-lg shadow-lg min-w-[140px]"
          >
            <button
              v-for="option in filterOptions"
              :key="option.value"
              class="block w-full px-3 py-1.5 text-xs text-left text-n-slate-11 hover:bg-n-alpha-black2 hover:text-n-slate-12 whitespace-nowrap first:rounded-t-lg last:rounded-b-lg"
              :class="{
                'font-semibold text-n-slate-12 bg-n-alpha-black2':
                  props.modelValue.filterType === option.value,
              }"
              @click.stop="selectFilterType(option.value)"
            >
              {{ $t(option.label) }}
            </button>
          </div>
        </div>
      </div>

      <!-- Team multiselect (below the filter row) -->
      <div v-if="props.modelValue.filterType === 'teams'" class="w-full">
        <multiselect
          v-model="selectedTeams"
          :options="teamList"
          track-by="id"
          label="name"
          multiple
          :close-on-select="false"
          :clear-on-select="false"
          hide-selected
          placeholder="..."
          selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
        />
      </div>
    </div>
  </div>
</template>
